/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-use-before-define */
import errorReporter from '@app/errorReporter';
import { Titles } from '@bb/users';
import { Alert, VerticalSpace } from '@brokerbay/design-components';
import { OnboardingController } from 'api';
import {
  Button,
  Icon,
  Link,
  message,
  Modal,
  Text,
  breakpoints,
  Title,
  DatePicker,
} from 'components';
import {
  startOfDay,
  addDays,
  isAfter,
  getDay,
  isBefore,
  isEqual,
} from 'date-fns';
import React from 'react';
import styled from 'styled-components';
import { featureEnabled, SUPPORT_EMAIL, UPGRADE_EMAIL } from 'utils';
import {
  AuthContext,
  SetupBrokerageWizardModalContext,
  OnboardingWelcomeModalContext,
  AdminContext,
} from 'contexts';
import type { PortalWithAssociationDataConfig } from 'Router/onboarding/types';
import { getOnboardingPortalLaunchDatesAssociations } from 'api/OnboardingPortalController';

type ModalState = 'setupBrokerage' | 'emailConsent';

export const SetupBrokerageWizardModal: React.FC = () => {
  const {
    auth,
    fetchOnboarding,
    fetchOrgStatus,
    isOrgPlanUpgraded,
    onboardingPortalConfig: { titles },
  } = React.useContext(AuthContext);

  const [onboardingPortalData, setOnboardingPortalData] =
    React.useState<PortalWithAssociationDataConfig | null>(null);

  const { onboardingPortalIds } = React.useContext(AdminContext);

  const { visible, closeModal, launchSetting } = React.useContext(
    SetupBrokerageWizardModalContext,
  );

  const [modalState, setModalState] =
    React.useState<ModalState>('setupBrokerage');

  const [selectedLaunchDate, setSelectedLaunchDate] =
    React.useState<Date | null>(null);

  const [saving, setSaving] = React.useState(false);

  const { setIsDoneWelcomeFlow } = React.useContext(
    OnboardingWelcomeModalContext,
  );

  const handleModalClose = (shouldOpenPrelaunchModal = false) => {
    setModalState('setupBrokerage');
    closeModal(shouldOpenPrelaunchModal);
    setIsDoneWelcomeFlow(true);
  };

  const handleModalNext = () => {
    setModalState('emailConsent');
  };

  const handleModalBack = () => {
    setModalState('setupBrokerage');
  };

  const handleSubmit = async () => {
    if (saving) return;
    if (!selectedLaunchDate) return;

    try {
      setSaving(true);

      await OnboardingController.completeWizard({
        launchDate: selectedLaunchDate,
        ...(isOrgPlanUpgraded ? { updateSubscription: true } : {}),
      });

      // Re-fetch org status
      await fetchOrgStatus();
      await fetchOnboarding();

      handleModalClose(true);
    } catch (err) {
      let msg = err.message;
      if (err.isAxiosError && err.response?.data?.message) {
        msg = err.response.data.message;
      }
      errorReporter.error(err);
      message.error(`Failed to setup brokerage: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const _canSubmit = () => !!selectedLaunchDate;

  const _buildTitle = () => {
    if (modalState === 'setupBrokerage') {
      return 'Select a Launch Date';
    }
    if (modalState === 'emailConsent') {
      return (
        <ModalTitleContainer>
          <IconContainer onClick={handleModalBack}>
            <Icon type="antdArrowLeftOutlined" />
          </IconContainer>
          <span>Email Consent</span>
        </ModalTitleContainer>
      );
    }
  };

  const _buildContent = () => {
    if (modalState === 'setupBrokerage') {
      const EMAIL = isOrgPlanUpgraded ? UPGRADE_EMAIL : SUPPORT_EMAIL;

      return (
        <VerticalSpace size={8}>
          <Title>When would you like to Launch?</Title>
          <Text type="secondary" block>
            {isOrgPlanUpgraded
              ? 'Your launch date is the day that your brokerage will officially begin using BrokerBay’s full Brokerage ecosystem.'
              : 'Your launch date is the day that your brokerage will officially begin using BrokerBay. ' +
                'Your online MLS appointment links on your brokerages listings will transfer to BrokerBay on this day.'}
          </Text>
          <StyledAlert
            type="info"
            banner
            message={
              <Text>
                To modify or cancel this launch date after saving, you will have
                to contact
                <Link href={`mailto:${EMAIL}`}> {EMAIL}</Link>
              </Text>
            }
          />

          {launchSetting && (
            <>
              <Text>Launch Date</Text>
              <StyledDatePicker
                mode="date"
                placeholder="Select Date"
                value={selectedLaunchDate}
                onChange={date => setSelectedLaunchDate(date)}
                disabledDate={date => {
                  if (!launchSetting) return true;

                  const isNotTwoDaysAfter = (() => {
                    const oneDaysAfter = addDays(new Date(), 1);
                    return !isAfter(startOfDay(date), startOfDay(oneDaysAfter));
                  })();

                  const isNotAvailableWeekday = (() => {
                    const weekday = getDay(date);
                    return !launchSetting.availableDaysWeek[weekday];
                  })();

                  const isBlockedSpecialDays = (() => Boolean(
                      launchSetting.blockedSpecialDays.find(x =>
                        isEqual(startOfDay(date), startOfDay(x)),
                      ),
                    ))();

                  const isOutOfDateRange = (() => {
                    if (launchSetting.launchMethod === 'ROLLING_LAUNCH') {
                      return Boolean(
                        (launchSetting.launchDateStart &&
                          isBefore(
                            startOfDay(date),
                            startOfDay(launchSetting.launchDateStart),
                          )) ||
                          (launchSetting.launchDateEnd &&
                            isAfter(
                              startOfDay(date),
                              startOfDay(launchSetting.launchDateEnd),
                            )),
                      );
                    }

                    return false;
                  })();

                  return (
                    isNotTwoDaysAfter ||
                    isNotAvailableWeekday ||
                    isBlockedSpecialDays ||
                    isOutOfDateRange
                  );
                }}
                getPopupContainer={el => el.parentElement || el}
              />
            </>
          )}
        </VerticalSpace>
      );
    }
    if (modalState === 'emailConsent') {
      return (
        <Text type="secondary" block>
          {`I, ${auth.firstName} ${auth.lastName}, ${
            titles[auth.title || Titles.Administrator].label
          } for ${
            auth.org.name
          }, ("Brokerage"), authorize PropTx Innovations Inc. ("PropTx") to provide the email addresses for salespersons, brokers and brokerage staff associated with my Brokerage to BrokerBay Inc. ("BrokerBay"), for use by BrokerBay for the sole purpose of BrokerBay providing the services described in the Master Subscription and Services Agreement between BrokerBay and PropTx (the “Agreement”), including as necessary for the setup, implementation and delivery of BrokerBay’s platform pursuant to the Agreement and any services to be provided by BrokerBay to my Brokerage under separate agreement between BrokerBay and my Brokerage relating to the Agreement. These email addresses are to be treated as Customer Data under the Agreement which may only be accessed, used or disclosed by BrokerBay solely to the extent required for such purpose, or as required by applicable law. I confirm to PropTx that Brokerage has all rights, consents, permissions and legal authority as may be necessary for me to authorize PropTx to provide these email addresses to BrokerBay for such purpose.`}
        </Text>
      );
    }
  };

  const _buildFooterAction = () => {
    if (modalState === 'setupBrokerage') {
      if (auth.org?.onboardingPortalId === onboardingPortalIds.TRREB) {
        return (
          <Button
            key="next"
            type="primary"
            onClick={handleModalNext}
            loading={saving}
            disabled={!_canSubmit()}
          >
            Next
          </Button>
        );
      }
      return (
        <Button
          key="save"
          type="primary"
          onClick={handleSubmit}
          loading={saving}
          disabled={!_canSubmit()}
        >
          Save
        </Button>
      );
    }

    if (modalState === 'emailConsent') {
      return (
        <Button
          key="agree-submit"
          type="primary"
          onClick={handleSubmit}
          loading={saving}
          disabled={!_canSubmit()}
        >
          Agree and Submit
        </Button>
      );
    }

    return null;
  };

  React.useEffect(() => {
    const fetchOnboardingData = async () => {
      const { onboardingPortalId } = auth.org;
      if (!onboardingPortalId) return;

      const portalData = await getOnboardingPortalLaunchDatesAssociations(
        onboardingPortalId,
        auth.org.id,
      );
      if (portalData) {
        setOnboardingPortalData(portalData);
      }
    };

    if (visible) {
      fetchOnboardingData();
    }
  }, [visible]);

  return (
    <Modal
      open={visible}
      title={_buildTitle()}
      footer={[_buildFooterAction()]}
      width={breakpoints.sm}
      onCancel={() => handleModalClose()}
      destroyOnClose
    >
      {_buildContent()}
    </Modal>
  );
};

const StyledAlert = styled(Alert)`
  margin: 16px 0;
`;

const IconContainer = styled.div`
  cursor: pointer;
  margin-right: 17px;
`;

const ModalTitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  background-color: rgb(245, 245, 245);
`;
